/**
 * Card-on-file chooser (DNKADM-0253) — the ONE component both money dialogs use to sell
 * to a member without re-entering their card. Built once and shared by
 * {@link SellCartDialog} and {@link BuyMembershipOrderDialog}; neither owns a copy.
 *
 * **The list is NEVER cached.** It is loaded by this component, for exactly as long as
 * this component is mounted — and the dialogs mount it only while they are open in card
 * mode, so a mount IS an open. Nothing holds the list beyond that: a stale list means
 * charging a card that no longer exists.
 *
 * **Selling is the priority, the chooser is a convenience.** A failed list load must
 * never block a sale: it degrades to a quiet inline note and the dialog's own
 * `CardPaymentField` takes the card as it always did. The same is true of a member with
 * no cards — nothing is rendered at all, rather than an empty picker.
 *
 * **Expiry is flagged, not enforced.** An expired card stays selectable (the server
 * decides whether it can be charged) but is visibly marked, exactly as the Payment
 * methods tab marks it — both read it from the shared `./memberCardDisplay`. A card with
 * a null expiry is NOT expired; absence is never evidence.
 *
 * **One card is preselected; several are not** (DNKADM-0476 F-02, Peyton 2026-10-27). There is
 * no default-card flag on the list, so when the member has exactly one card on file it is
 * chosen once the list loads; with two or more nothing is chosen and the desk picks. It happens
 * once per mount (a mount is an open in card mode): "Use a new card" afterwards sticks.
 *
 * White-label: `cardBrand`/`maskedPan` are backend text and are scrubbed by
 * `cardLabel`. `cardId` is **OPAQUE** — it is the radio's value on the wire of the DOM
 * and is never parsed or rendered as text.
 */
import { useCallback, useEffect, useId, useRef } from 'react';
import {
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { listMemberCards } from '@/api/memberCards';
import { useResource } from '@/hooks/useResource';
import { inkAlpha } from '@/theme';
import { cardLabel, expiryLabel, isCardExpired } from './memberCardDisplay';

/** The radio value that means "none of these — take a new card". */
const NEW_CARD = '';

export interface CardOnFileChooserProps {
  contactId: string;
  /** The selected card, or `null` when the admin is entering a new one. */
  value: string | null;
  onChange: (cardId: string | null) => void;
  disabled?: boolean;
  excludeCardIds?: string[];
}

export function CardOnFileChooser({
  contactId,
  value,
  onChange,
  disabled = false,
  excludeCardIds = [],
}: CardOnFileChooserProps) {
  const theme = useTheme();
  const labelId = `${useId()}-card-on-file`;
  const cards = useResource(
    useCallback(() => listMemberCards(contactId), [contactId]),
    [contactId],
  );

  const excluded = new Set(excludeCardIds);
  const rows = (cards.data ?? []).filter((card) => !excluded.has(card.cardId));
  const selectable = value !== null && rows.some((card) => card.cardId === value);

  /**
   * Never keep a selection the freshly-loaded list does not contain. The dialogs already
   * clear the selection whenever this component unmounts, so this is defence in depth —
   * but it is the rule the whole spec turns on: a card that is no longer on file must
   * never be the credential a sale is charged to.
   */
  useEffect(() => {
    if (value !== null && cards.status === 'ready' && !selectable) onChange(null);
  }, [cards.status, onChange, selectable, value]);

  // The only card on file is preselected, once, when the list first loads (F-02).
  const onlyCardId = rows.length === 1 ? (rows[0]?.cardId ?? null) : null;
  const preselectDoneRef = useRef(false);
  useEffect(() => {
    if (preselectDoneRef.current || cards.status !== 'ready') return;
    preselectDoneRef.current = true;
    if (!disabled && value === null && onlyCardId !== null) onChange(onlyCardId);
  }, [cards.status, disabled, onChange, onlyCardId, value]);

  if (cards.status === 'loading') {
    return (
      <Typography variant="caption" sx={{ color: inkAlpha(theme, 0.62) }}>
        Checking for cards on file...
      </Typography>
    );
  }

  if (cards.status === 'error') {
    // Quiet, and deliberately NOT an alert: the sale carries on with a new card.
    return (
      <Typography variant="caption" sx={{ color: inkAlpha(theme, 0.62) }}>
        Cards on file could not be loaded. Enter the card below instead.
      </Typography>
    );
  }

  // No cards on file ⇒ no chooser at all; the dialog's card field is the whole flow.
  if (rows.length === 0) return null;

  const now = new Date();
  return (
    <FormControl disabled={disabled}>
      <FormLabel id={labelId} sx={{ fontSize: 13 }}>
        Card on file
      </FormLabel>
      <RadioGroup
        aria-labelledby={labelId}
        value={selectable ? value : NEW_CARD}
        onChange={(_event, next) => onChange(next === NEW_CARD ? null : next)}
      >
        {/* Server order is preserved — newest first, never re-sorted here. */}
        {rows.map((card) => {
          const expired = isCardExpired(card, now);
          return (
            <FormControlLabel
              key={card.cardId}
              value={card.cardId}
              control={<Radio size="small" />}
              label={
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 14 }}>
                    {`${cardLabel(card)} · ${expiryLabel(card)}`}
                  </Typography>
                  {/* Expired cards stay SELECTABLE — the server decides — but must be
                      visibly flagged so the desk is never surprised by a decline. */}
                  {expired && (
                    <Chip
                      size="small"
                      color="warning"
                      label="Expired"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  )}
                </Stack>
              }
            />
          );
        })}
        <FormControlLabel
          value={NEW_CARD}
          control={<Radio size="small" />}
          label={<Typography sx={{ fontSize: 14 }}>Use a new card</Typography>}
        />
      </RadioGroup>
    </FormControl>
  );
}
